/**
 * 设备ID生成工具
 * 根据用户信息和环境生成唯一的设备标识
 */

import type { UserInfo } from '@/store/user'

/**
 * 生成随机UUID (简化版)
 */
function generateRandomId(): string {
  return Math.random().toString(36).substring(2, 10) + 
         Math.random().toString(36).substring(2, 10)
}

/**
 * 生成设备ID
 * @param userInfo 用户信息
 * @param isDev 是否为开发环境
 * @returns 生成的设备ID
 */
export function generateDeviceId(userInfo: UserInfo | null, isDev: boolean = false): string {
  // 开发环境
  if (isDev) {
    if (userInfo?.username) {
      return `web_dev_${userInfo.username}`
    }
    return `web_dev_${Date.now()}`
  }

  // 生产环境 - 已登录用户
  if (userInfo?.userId) {
    const timestamp = Date.now()
    return `web_${userInfo.userId}_${timestamp}`
  }

  // 生产环境 - 测试账号（使用邮箱）
  if (userInfo?.email) {
    // 移除邮箱中的特殊字符
    const safeEmail = userInfo.email.replace(/[^a-zA-Z0-9]/g, '_')
    return `web_test_${safeEmail}`
  }

  // 生产环境 - 未登录用户（访客）
  const randomId = generateRandomId()
  return `web_guest_${randomId}`
}

/**
 * 验证设备ID格式
 * @param deviceId 设备ID
 * @returns 是否有效
 */
export function validateDeviceId(deviceId: string): boolean {
  if (!deviceId || deviceId.length < 3 || deviceId.length > 64) {
    return false
  }
  // 仅允许字母、数字和下划线
  return /^[a-zA-Z0-9_]+$/.test(deviceId)
}

/**
 * 从LocalStorage获取或生成设备ID
 * @param userInfo 用户信息
 * @param isDev 是否为开发环境
 * @returns 设备ID
 */
export function getOrCreateDeviceId(userInfo: UserInfo | null, isDev: boolean = false): string {
  // 尝试从LocalStorage读取
  const stored = localStorage.getItem('deviceId')
  if (stored && validateDeviceId(stored)) {
    return stored
  }

  // 生成新的设备ID
  const newDeviceId = generateDeviceId(userInfo, isDev)
  
  // 保存到LocalStorage
  try {
    localStorage.setItem('deviceId', newDeviceId)
  } catch (e) {
    console.error('Failed to save deviceId to localStorage:', e)
  }

  return newDeviceId
}

/**
 * 清除设备ID（用户登出时调用）
 */
export function clearDeviceId(): void {
  try {
    localStorage.removeItem('deviceId')
  } catch (e) {
    console.error('Failed to remove deviceId from localStorage:', e)
  }
}
